import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { X, EnvelopeSimple, Eye, ArrowClockwise, Warning, PaperPlaneTilt } from '@phosphor-icons/react';
import { RichTextEditor, stripHtml } from '@/components/RichTextEditor';

// ---------------------------------------------------------------------------
// Decision email composer shared by rolling notification batches and closure.
// Kanban moves remain silent. Sending explicitly notifies only outcomes that
// have not already been emailed to that recipient.
// ---------------------------------------------------------------------------

export const CLOSE_EMAIL_CATEGORIES = ['accepted', 'waitlisted', 'rejected'];

// Tab chrome per outcome. `ring`/`bg` drive the active tab, `dot` the count pill.
const CATEGORY_THEME = {
  accepted: {
    label: 'Accepted',
    tab: 'bg-success text-white border-success',
    idle: 'border-success/30 text-success hover:bg-success/10',
    panel: 'border-success/40 bg-success/5',
    accent: 'text-success',
    pill: 'bg-success/15 text-success',
  },
  waitlisted: {
    label: 'Waitlisted',
    tab: 'bg-warning text-black border-warning',
    idle: 'border-warning/30 text-warning hover:bg-warning/10',
    panel: 'border-warning/40 bg-warning/5',
    accent: 'text-warning',
    pill: 'bg-warning/15 text-warning',
  },
  rejected: {
    label: 'Not selected',
    tab: 'bg-danger text-white border-danger',
    idle: 'border-danger/30 text-danger hover:bg-danger/10',
    panel: 'border-danger/40 bg-danger/5',
    accent: 'text-danger',
    pill: 'bg-danger/15 text-danger',
  },
};

const FALLBACK_TOKENS = [
  { token: '{{startup_name}}', label: 'Startup name' },
  { token: '{{program_name}}', label: 'Program name' },
  { token: '{{fund_name}}', label: 'Your fund name' },
];

// Strip drafts down to what the API wants.
export const toEmailPayload = (drafts) =>
  CLOSE_EMAIL_CATEGORIES.reduce((acc, key) => {
    const d = drafts?.[key];
    if (!d) return acc;
    acc[key] = { enabled: !!d.enabled, subject: d.subject || '', body_html: d.body_html || '' };
    return acc;
  }, {});

// ---------------------------------------------------------------------------
// useCloseEmailDrafts — loads starter copy + per-lane recipient counts.
// ---------------------------------------------------------------------------
export function useCloseEmailDrafts(programId, rolling = false, category = null) {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState(null);
  const [meta, setMeta] = useState({ tokens: FALLBACK_TOKENS, receivedCount: 0, programName: '', fundName: '' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/vc/programs/${programId}/close-emails${rolling ? "?rolling=true" : ""}`)
      .then((r) => {
        if (cancelled) return;
        const next = {};
        (r.data.categories || []).forEach((c) => {
          next[c.key] = {
            label: c.label,
            enabled: category ? c.key === category : c.enabled,
            subject: c.subject,
            body_html: c.body_html,
            defaultSubject: c.default_subject,
            defaultBody: c.default_body_html,
            recipientCount: c.recipient_count,
            recipients: c.recipients || [],
          };
        });
        setDrafts(next);
        setMeta({
          tokens: r.data.tokens?.length ? r.data.tokens : FALLBACK_TOKENS,
          receivedCount: r.data.received_count || 0,
          programName: r.data.program_name || '',
          fundName: r.data.fund_name || '',
          alreadySent: !!r.data.already_sent,
        });
      })
      .catch(() => { if (!cancelled) toast.error('Could not load the email drafts'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [programId, rolling, category]);

  return { loading, drafts, setDrafts, meta };
}

// ---------------------------------------------------------------------------
// CloseEmailComposer — the three-tab editor.
// ---------------------------------------------------------------------------
export function CloseEmailComposer({
  drafts,
  setDrafts,
  tokens = FALLBACK_TOKENS,
  receivedCount = 0,
  includeReceived,
  setIncludeReceived,
  categories = CLOSE_EMAIL_CATEGORIES,
}) {
  const [active, setActive] = useState(categories[0]);
  // RichTextEditor seeds its content once on mount, so swapping tabs (or
  // resetting to the default copy) has to remount it — hence the nonce.
  const [nonce, setNonce] = useState(0);
  const bodyWrapRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocused = useRef('body');

  const draft = drafts?.[active];
  const theme = CATEGORY_THEME[active];

  const patch = (changes) => setDrafts((d) => ({ ...d, [active]: { ...d[active], ...changes } }));

  const resetToDefault = () => {
    patch({ subject: draft.defaultSubject, body_html: draft.defaultBody });
    setNonce((n) => n + 1);
    toast.success('Reset to the default copy');
  };

  // Insert a {{token}} wherever the cursor last was — subject field or body.
  const insertToken = (token) => {
    if (lastFocused.current === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      const next = `${el.value.slice(0, start)}${token}${el.value.slice(end)}`;
      patch({ subject: next });
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
      return;
    }
    const el = bodyWrapRef.current?.querySelector('[contenteditable]');
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    // Caret may be outside the editor (first click on a token) — park it at the end.
    if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    document.execCommand('insertText', false, token);
    patch({ body_html: el.innerHTML });
  };

  if (!draft) return null;

  const bodyEmpty = !stripHtml(draft.body_html || '').trim();
  const subjectEmpty = !(draft.subject || '').trim();

  return (
    <div data-testid="close-email-composer">
      {/* Outcome tabs — same colours as the kanban lanes */}
      <div className="flex items-center gap-2 mb-3">
        {categories.map((key) => {
          const t = CATEGORY_THEME[key];
          const d = drafts[key] || {};
          const isActive = key === active;
          return (
            <button
              key={key}
              type="button"
              onClick={() => { setActive(key); setNonce((n) => n + 1); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-bold transition-colors ${isActive ? t.tab : `bg-background ${t.idle}`}`}
              data-testid={`close-email-tab-${key}`}
            >
              {t.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isActive ? 'bg-black/20' : t.pill}`}>
                {d.recipientCount ?? 0}
              </span>
              {!d.enabled && <span className="text-[9px] uppercase tracking-wider opacity-80">off</span>}
            </button>
          );
        })}
      </div>

      <div className={`rounded-lg border p-4 ${theme.panel}`}>
        {/* Recipients + on/off */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className={`text-xs font-bold ${theme.accent}`}>
              {draft.recipientCount} recipient{draft.recipientCount === 1 ? '' : 's'}
            </div>
            <div className="text-[11px] text-text-muted truncate" title={(draft.recipients || []).map((r) => r.email).join(', ')}>
              {draft.recipientCount === 0
                ? 'No applicants in this lane — nothing will be sent.'
                : (draft.recipients || []).slice(0, 3).map((r) => r.email).join(', ')
                  + (draft.recipientCount > 3 ? ` +${draft.recipientCount - 3} more` : '')}
            </div>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-text-secondary cursor-pointer flex-shrink-0 whitespace-nowrap">
            <input
              type="checkbox"
              checked={!!draft.enabled}
              disabled={draft.recipientCount === 0}
              onChange={(e) => patch({ enabled: e.target.checked })}
              data-testid={`close-email-enabled-${active}`}
            />
            Send this email
          </label>
        </div>

        <div className={draft.enabled && draft.recipientCount > 0 ? '' : 'opacity-50 pointer-events-none'}>
          <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 font-semibold">Subject</label>
          <input
            ref={subjectRef}
            type="text"
            value={draft.subject || ''}
            onFocus={() => { lastFocused.current = 'subject'; }}
            onChange={(e) => patch({ subject: e.target.value })}
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary mb-3"
            placeholder={draft.defaultSubject}
            data-testid={`close-email-subject-${active}`}
          />

          <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 font-semibold">Message</label>
          <div ref={bodyWrapRef} onFocus={() => { lastFocused.current = 'body'; }}>
            <RichTextEditor
              key={`${active}-${nonce}`}
              value={draft.body_html || ''}
              onChange={(html) => patch({ body_html: html })}
              placeholder="Write the message this group will receive…"
              data-testid={`close-email-body-${active}`}
            />
          </div>

          {/* Tokens */}
          <div className="flex items-center flex-wrap gap-1.5 mt-2.5">
            <span className="text-[10px] uppercase tracking-wider text-text-muted mr-0.5">Insert</span>
            {tokens.map((t) => (
              <button
                key={t.token}
                type="button"
                title={`${t.label} — inserted at your cursor`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertToken(t.token)}
                className="px-2 py-1 rounded border border-border bg-background text-[10px] font-mono text-text-secondary hover:border-primary hover:text-primary"
                data-testid={`close-email-token-${t.token.replace(/[{}]/g, '')}`}
              >
                {t.token}
              </button>
            ))}
            <button
              type="button"
              onClick={resetToDefault}
              className="ml-auto flex items-center gap-1 text-[10px] text-text-muted hover:text-text-primary"
              data-testid={`close-email-reset-${active}`}
            >
              <ArrowClockwise size={11} weight="bold" /> Reset to default
            </button>
          </div>

          {(subjectEmpty || bodyEmpty) && (
            <div className="flex items-center gap-1.5 mt-2 text-[11px] text-warning">
              <Warning size={12} weight="fill" />
              {subjectEmpty && bodyEmpty ? 'Subject and message are empty — the default copy will be sent.'
                : subjectEmpty ? 'Subject is empty — the default subject will be used.'
                  : 'Message is empty — the default copy will be sent.'}
            </div>
          )}
        </div>

        {/* Untriaged applicants only concern the "not selected" tab. */}
        {active === 'rejected' && receivedCount > 0 && (
          <label className="flex items-start gap-2 mt-4 pt-3 border-t border-border/60 text-[11px] text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={!!includeReceived}
              onChange={(e) => setIncludeReceived(e.target.checked)}
              className="mt-0.5"
              data-testid="close-email-include-received"
            />
            <span>
              Also send this to the <strong className="text-text-primary">{receivedCount}</strong> applicant{receivedCount === 1 ? '' : 's'} still
              sitting in <strong className="text-text-primary">Application Received</strong>.
              {' '}Leave it off and they get a neutral “applications are closed” note instead of a rejection.
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CloseEmailPreview — renders the real emails, straight from the backend, so
// what you see is byte-for-byte what lands in the founder's inbox.
// ---------------------------------------------------------------------------
export function CloseEmailPreview({ programId, drafts, includeReceived, onClose, rolling = false, editing = false }) {
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState([]);
  const [active, setActive] = useState('accepted');
  const [receivedCount, setReceivedCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.post(`/vc/programs/${programId}/close-emails/preview`, {
      emails: toEmailPayload(drafts),
      include_received_in_rejected: !!includeReceived,
      rolling,
    })
      .then((r) => {
        if (cancelled) return;
        setPreviews(r.data.previews || []);
        setReceivedCount(r.data.received_count || 0);
        const firstOn = (r.data.previews || []).find((p) => p.enabled && p.recipient_count > 0);
        if (firstOn) setActive(firstOn.key);
      })
      .catch(() => { if (!cancelled) toast.error('Could not render the preview'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [programId, drafts, includeReceived, rolling]);

  const current = useMemo(() => previews.find((p) => p.key === active), [previews, active]);
  const totalOn = previews.filter((p) => p.enabled).reduce((n, p) => n + p.recipient_count, 0);

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="close-email-preview">
      <div className="bg-surface rounded-xl border border-border w-full max-w-3xl h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border flex-shrink-0">
          <div>
            <h3 className="text-base font-heading font-bold text-text-primary flex items-center gap-2">
              <Eye size={16} weight="bold" className="text-primary" /> Preview — what each group receives
            </h3>
            <div className="text-[11px] text-text-muted">
              {editing ? "Template preview only. Saving does not send emails." : `${totalOn} email${totalOn === 1 ? '' : 's'} ${rolling ? "will be sent now without changing application availability." : "will be sent when you close."}`}
              {receivedCount > 0 && ` ${receivedCount} untriaged applicant${receivedCount === 1 ? '' : 's'} get the neutral closure note.`}
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="close-preview-btn"><X size={18} /></button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-border flex-shrink-0">
          {previews.map((p) => {
            const t = CATEGORY_THEME[p.key];
            const isActive = p.key === active;
            return (
              <button
                key={p.key}
                onClick={() => setActive(p.key)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-colors ${isActive ? t.tab : `bg-background ${t.idle}`} ${p.enabled ? '' : 'opacity-50'}`}
                data-testid={`preview-tab-${p.key}`}
              >
                {t.label}
                <span className={`text-[10px] font-mono px-1.5 rounded ${isActive ? 'bg-black/20' : t.pill}`}>{p.recipient_count}</span>
                {!p.enabled && <span className="text-[9px] uppercase">off</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-text-muted text-sm">Rendering emails…</div>
        ) : !current ? (
          <div className="flex-1 flex items-center justify-center text-text-muted text-sm">Nothing to preview.</div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-border bg-background flex-shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Subject</div>
              <div className="text-sm font-semibold text-text-primary" data-testid="preview-subject">{current.subject}</div>
              <div className="text-[11px] text-text-muted mt-1.5">
                {current.recipient_count === 0
                  ? 'No applicants in this lane — this email will not be sent.'
                  : <>To: {current.recipients.slice(0, 4).join(', ')}{current.recipient_count > 4 ? ` +${current.recipient_count - 4} more` : ''}
                    {current.sample_recipient && <> · previewed as <strong className="text-text-secondary">{current.sample_recipient}</strong></>}</>}
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-[#F8FAFC]">
              {/* Sandboxed so the rendered email can't touch the app. */}
              <iframe
                title={`${current.label} email preview`}
                srcDoc={current.html}
                sandbox=""
                className="w-full h-full border-0"
                data-testid="preview-frame"
              />
            </div>
          </>
        )}

        <div className="px-5 py-3 border-t border-border flex justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg" data-testid="preview-done">
            Back to editing
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SendDecisionEmailsModal — explicit rolling batches, independent of closure.
// ---------------------------------------------------------------------------
export function SendDecisionEmailsModal({ program, onClose, onSent, category = null, editOnly = false }) {
  const { loading, drafts, setDrafts, meta } = useCloseEmailDrafts(program.id, true, category);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const totalToSend = CLOSE_EMAIL_CATEGORIES.reduce(
    (n, k) => n + (drafts?.[k]?.enabled ? (drafts[k].recipientCount || 0) : 0), 0,
  );

  const send = async () => {
    if (sending || (!editOnly && totalToSend === 0)) return;
    setSending(true);
    try {
      if (editOnly) {
        await api.put(`/vc/programs/${program.id}/close-emails`, { emails: toEmailPayload(drafts) });
        toast.success('Decision email templates saved. No emails were sent.');
        onSent();
        return;
      }
      const r = await api.post(`/vc/programs/${program.id}/close-emails/send`, {
        emails: toEmailPayload(drafts),
        include_received_in_rejected: false,
        category,
        confirm: true,
      });
      if (r.data.failed) toast.warning(`${r.data.emails_total} sent; ${r.data.failed} failed. Reopen to retry failed emails.`);
      else if (r.data.emails_total) toast.success(`${r.data.emails_total} decision email${r.data.emails_total === 1 ? '' : 's'} sent.`);
      else toast.info('No new decisions to send. Previously notified outcomes were skipped.');
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" data-testid={editOnly ? "edit-decision-emails-modal" : "send-decision-emails-modal"}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
            <EnvelopeSimple size={18} weight="fill" className="text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-heading font-bold text-text-primary">{editOnly ? "Edit decision emails" : category ? `Notify ${CATEGORY_THEME[category].label.toLowerCase()} applicants` : "Send decision emails"}</h2>
            <div className="text-xs text-text-secondary truncate">
              {editOnly ? "Edit acceptance, waitlist, and rejection templates. Save them for future notifications." : "Notify reviewed applicants in batches. Sending does not close applications."}
            </div>
          </div>
          <button disabled={sending} onClick={onClose} className="ml-auto text-text-muted hover:text-text-primary"><X size={18} /></button>
        </div>

        {loading || !drafts ? (
          <div className="py-16 text-center text-sm text-text-muted">Loading drafts…</div>
        ) : (
          <>
            <CloseEmailComposer
              drafts={drafts}
              setDrafts={setDrafts}
              tokens={meta.tokens}
              receivedCount={0}
              categories={category ? [category] : CLOSE_EMAIL_CATEGORIES}
            />
            <div className="flex items-center gap-3 mt-5">
              <button disabled={sending} onClick={onClose} className="px-4 py-2.5 bg-background border border-border text-text-primary rounded-lg font-medium hover:border-primary">Cancel</button>
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-primary text-primary rounded-lg font-semibold hover:bg-primary/10"
                data-testid="open-preview-btn"
              >
                <Eye size={14} weight="bold" /> Preview emails
              </button>
              <button
                onClick={send}
                disabled={sending || (!editOnly && totalToSend === 0)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary text-white rounded-lg font-semibold disabled:opacity-50"
                data-testid={editOnly ? "save-decision-emails-btn" : "send-decision-emails-btn"}
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {sending ? (editOnly ? 'Saving…' : 'Sending…') : editOnly ? 'Save templates' : `Send ${totalToSend} email${totalToSend === 1 ? '' : 's'}`}
              </button>
            </div>
            <div className="text-[11px] text-text-muted mt-2 flex items-center gap-1.5">
              <Warning size={12} weight="fill" className="text-warning" /> {editOnly ? "Saving only updates the templates. Use a column’s Notify button to send emails." : "Only applicants not yet notified of their current status are emailed. Undecided applicants receive nothing. You can send another batch later."}
            </div>
          </>
        )}
      </div>

      {showPreview && drafts && (
        <CloseEmailPreview
          programId={program.id}
          drafts={drafts}
          includeReceived={false}
          rolling
          editing={editOnly}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

export default CloseEmailComposer;
